/* Live Mirror ✦ — host-side preview of what the venue sees.
 *
 * Operational visibility. Sits inside the Setup tab as a 16:9 tile.
 * The host can watch their own Live Screen from their phone while
 * the projector runs the venue — closing the operator-confidence
 * loop. They see the room's atmosphere, not just trust it.
 *
 * Implementation choice (iter 170): an iframe pointing at the
 * canonical live page with `?embed=1`. The live page detects embed
 * mode, skips the audio gate and fullscreen request, and stays
 * silent. The mirror is therefore a perfectly faithful render of
 * what the projector is showing right now — same SSE feed, same
 * scene rotation, same data — minus audio (which is venue-only).
 *
 * Why iframe instead of a re-composed mini-version?
 *   1. Truthfulness — the mirror IS the live page, not a copy. If
 *      the projector breaks, the mirror breaks the same way, which
 *      is the data the host needs.
 *   2. Zero duplication — every scene-rotation change automatically
 *      reflects in the mirror without touching this component.
 *   3. Iframe sandboxing — guarantees the mirror can't accidentally
 *      take over the host page or steal focus.
 */
import React, { useMemo, useState } from 'react';
import { GraphitePanel, MODE_TWO, QuietPress } from './ModeTwoPrimitives';

export default function LiveMirrorPreview({ event }) {
  const [now] = useState(() => Date.now()); // stable cache-buster on mount
  const slug = event?.slug || event?.event_id;
  const url = useMemo(() => {
    // Cache-bust on mount so reopening Setup picks up scene changes
    return `/event/${slug}/live?embed=1&t=${now}`;
  }, [slug, now]);

  // Only render when the event has the entitlement (universal on
  // every paid edition per iter 170, but we still gate so future
  // tier changes flow through one check).
  const hasLive = (event?.features || []).includes('live_screen') || !!event?.live_screen_purchased;
  if (!hasLive) return null;

  return (
    <GraphitePanel testId="live-mirror-panel" padding="clamp(20px, 2.4vw, 28px)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-end', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: MODE_TWO.gold,
            }}>✦ Operational visibility</div>
            <div style={{
              marginTop: 6, fontFamily: MODE_TWO.serif, fontSize: 22, color: MODE_TWO.ink,
            }}>Mirror what the venue sees ✦</div>
            <div style={{
              marginTop: 4, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 13, color: MODE_TWO.inkMuted, maxWidth: 420,
            }}>
              The live atmosphere, exactly as the projector is showing it. Silent
              here on purpose — audio plays only at the venue.
            </div>
          </div>
          <QuietPress
            onClick={() => window.open(`/event/${slug}/live`, '_blank', 'noopener')}
            testId="live-mirror-open-full"
            variant="quiet"
          >
            Open at full screen ✦
          </QuietPress>
        </div>

        {/* The mirror itself — pure 16:9 frame */}
        <div
          data-testid="live-mirror-frame"
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            background: '#0c0805',
            borderRadius: 6,
            overflow: 'hidden',
            border: `1px solid ${MODE_TWO.hairSoft}`,
            boxShadow: 'inset 0 0 0 1px rgba(193,154,75,0.08)',
          }}
        >
          <iframe
            title="Live Screen mirror"
            src={url}
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              border: 'none',
              pointerEvents: 'none', // host shouldn't drive the venue display
            }}
          />
          {/* Subtle "live" indicator — top-left corner */}
          <div
            data-testid="live-mirror-status"
            style={{
              position: 'absolute', top: 12, left: 14, zIndex: 2,
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '4px 10px', borderRadius: 999,
              background: 'rgba(20,18,32,0.6)',
              border: '1px solid rgba(193,154,75,0.25)',
              backdropFilter: 'blur(6px)',
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.24em',
              color: 'rgba(245,239,223,0.78)', textTransform: 'uppercase',
              pointerEvents: 'none',
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#c19a4b', boxShadow: '0 0 8px rgba(193,154,75,0.65)',
              animation: 'liveMirrorPulse 2.4s ease-in-out infinite',
            }} />
            Live
          </div>
          <style>{`
            @keyframes liveMirrorPulse {
              0%, 100% { opacity: 0.65; }
              50%      { opacity: 1; }
            }
          `}</style>
        </div>
      </div>
    </GraphitePanel>
  );
}
